import * as STD from '@emersya/mpa-std-lib';

let uid = 0;
type stages = (
	'initial' |
	'initialization' |
	'metaprogramming' |
	'treeNodes' |
	'configuration' |
	'ready'
	);

abstract class Manifest {
	static XMLNS = {
		MP: 'https://xmlns.emersya.com/mpa/metaProgramming',
		CM: 'https://xmlns.emersya.com/mpa/componentsManager',
		C: 'https://xmlns.emersya.com/mpa/components',
		XHTML: <string>document.documentElement.namespaceURI
	};

	static isMPData(
		element: Element | Attr,
		name: string
	) {
		return (
			(element.namespaceURI === this.XMLNS.MP) &&
			(element.localName === name)
		);
	};

	static retrieveMPAttributes(element: Element): Manifest.IMPAttribute[] {
		return Array.from(element.attributes).filter(x => (x.namespaceURI === this.XMLNS.MP)).map(x => ({
			key: !x.localName.startsWith('remap-') ? x.localName : 'remap',
			realKey: x.name,
			value: x.value,
			attributeNode: x
		}));
	};

	static create(
		path: string,
		source: string,
		expectedType?: 'manager' | 'component'
	): Manifest;
	static create(
		path: string,
		source: string,
		expectedType: 'manager'
	): Manifest.ManagerManifest;
	static create(
		path: string,
		source: string,
		expectedType: 'component',
		inheritedManifest: Manifest.ComponentManifest | null
	): Manifest.ComponentManifest;

	static create(
		path: string,
		source: string,
		expectedType?: 'manager' | 'component',
		inheritedManifest?: Manifest.ComponentManifest | null
	) {
		const sourceWithoutComment = (
			source.split('\n').
			map(x => <string>x.split('~~~~')[0]).
			map(x => (x.trim() === '') ? '' : x).
			filter(x => x.length).
			join('\n')
		);
		const document = <XMLDocument>STD.DOM.parse(sourceWithoutComment, 'application/xml');
		const initialXML = document.documentElement;
		const type = initialXML.getAttribute('type');
		if (type !== expectedType) {
			throw STD.errors.user(`Manifest<${path}> of type<${expectedType}>, received<${type}>!`, {
				path,
				source,
				initialXML,
				expectedType,
				type
			});
		}
		const Class = (
			(expectedType === 'manager') ? Manifest.ManagerManifest :
				(expectedType === 'component') ? Manifest.ComponentManifest :
					Manifest
		);
		const manifest = <Manifest>Reflect.construct(Class, [source, initialXML, expectedType, inheritedManifest]);
		manifest.initialize();
		return (manifest);
	};

	static configurationMarkerIndex = 0;

	static generateTreeNodes(
		childs: Element[],
		top: Component.ITreeNode[],
		configurations: Record<string, Component.IConfiguration>,
		parentPath: string,
		inheritedConfigurations: Record<string, Component.IConfiguration> = {}
	) {
		const stack = <[Component.ITreeNode[], Element, string][]>[
			...childs.map(x => [
				top,
				<Element>x,
				parentPath
			])
		];
		while (stack.length) {
			const [parentChilds, element, parentPath] = <[Component.ITreeNode[], Element, string]>stack.shift();
			if (element.namespaceURI === Manifest.XMLNS.MP) {
				continue;
			}
			const id = element.getAttribute('id');
			if (!id) {
				throw STD.errors.user('Node without id!', {
					element,
					childs
				});
			}
			const path = parentPath ? (parentPath + '.' + id) : id;
			const basicConfiguration: DOMComponent.IConfiguration = {
				'@marker': (this.configurationMarkerIndex++).toString(16)
			};
			{
				const inheritedConfiguration = inheritedConfigurations[path];
				if (inheritedConfiguration) {
					STD.object.deepMergeAssign(basicConfiguration, [inheritedConfiguration]);
				}
			}
			{
				const configuration = configurations[path];
				if (configuration) {
					STD.object.deepMergeAssign(configuration, [basicConfiguration]);
				} else {
					configurations[path] = basicConfiguration;
				}
			}
			if (element.textContent && (element.childNodes.length === 1)) {
				basicConfiguration['@stringData'] = element.textContent;
			}
			const classList = element.getAttributeNS(Manifest.XMLNS.XHTML, 'class');
			if (classList) {
				basicConfiguration['@classList'] = classList.split(' ');
			}
			const attrs = Array.from(element.attributes).map(x => <[string, string]>[x.nodeName, x.value]
			);
			const XHTMLDatasetAttributeName = (
				element.namespaceURI === Manifest.XMLNS.XHTML ? 'data-' : 'xhtml:data-'
			);
			const dataset = STD.object.fromKeyValueList(
				attrs.filter(x => x[0].startsWith(XHTMLDatasetAttributeName)).
				map(x => [x[0].replace(XHTMLDatasetAttributeName, ''), x[1]])
			);
			if (Object.keys(dataset).length > 0) {
				basicConfiguration['@dataset'] = dataset;
			}
			const node: Component.ITreeNode = {
				id,
				type: element.tagName,
				childs: [],
				configuration: basicConfiguration,
				isSlot: element.hasAttribute('cm:declare-slot'),
				declaredSlot: element.getAttribute('cm:declare-slot'),
				slot: element.getAttribute('cm:slot')
			};
			parentChilds.push(node);
			for (const child of Array.from(element.children)) {
				stack.push([node.childs, child, path]);
			}
		}
	};

	readonly source: string;
	readonly initialXML: Element;
	finalXML: Element;
	readonly type!: string | 'unknown';
	error: Error | null = null;
	stage: stages = 'initial';
	readonly dataSourcesGetter: Record<string, (target: string) => Element[]> = {};
	readonly configurations: Record<string, Component.IConfiguration> = {};
	readonly dynamicConfigurations: Record<string, Component.IConfiguration> = {};
	readonly deferedConfigurations: Record<string, Component.IConfiguration> = {};
	readonly treeNodes: Component.ITreeNode[] = [];
	readonly templates: Element[] = [];
	readonly inheritedManifest: Manifest | null;
	// Cancelled for now
	// readonly data              : { optimize? : boolean; } = {
	// };
	abstract instantiate(...x: unknown[]): Promise<Component<any, any>>;

	protected throwUserError(
		message: string,
		details?: Record<string, any>
	): never {
		throw STD.errors.user(message, Object.assign({
			manifest: this
		}, details));
	};

	protected throwLibraryError(
		message: string,
		details?: Record<string, any>
	): never {
		throw STD.errors.library(message, Object.assign({
			manifest: this
		}, details));
	};

	protected initializeManifestDetails(manifest: Element): void {
		const MPContainerAttr = manifest.getAttributeNS(Manifest.XMLNS.MP, 'container');
		const MPContainer: Record<string, any> | null = (
			MPContainerAttr ? (<any>window)[MPContainerAttr] :
				null
		);
		if (!manifest.details) {
			manifest.details = {};
			// this.throwLibraryError(RangeError, 'Manifest without details!');
		}
		manifest.details.MPContainer = MPContainer;
		const initializeElementDetails = this.initializeElementDetails.bind(this);
		for (const child of Array.from(manifest.children)) {
			STD.DOM.visitSync(child, initializeElementDetails);
		}
	};

	protected initializeElementDetails(element: Element): void {
		void element;
	};

	protected getRootElement(
		type: string,
		NS?: string,
		searchOnParent: boolean = true
	): Element | null {
		const childs = Array.from(this.finalXML.children);
		const rootElement = (
			NS ? (childs.find(x => (
				(x.namespaceURI === NS) &&
				(x.localName === type)
			))) : childs.find(x => x.tagName === type)
		);
		if (rootElement) {
			return rootElement;
		}
		if (searchOnParent && this.inheritedManifest) {
			return this.inheritedManifest.getRootElement(type, NS, searchOnParent);
		}
		return null;
	};

	protected getMandatoryRootElement(
		type: string,
		NS?: string,
		searchOnParent: boolean = true
	) {
		const element = this.getRootElement(type, NS, searchOnParent);
		if (!element) {
			this.throwUserError('Mandatory root element not found!', {
				type,
				NS
			});
		}
		return (element);
	};

	private generateTreeNodes() {
		const inherited = this.inheritedManifest;
		if (inherited) {
			if (inherited.stage !== 'ready') {
				inherited.initialize();
			}
			this.treeNodes.push(...inherited.treeNodes.map(node => STD.object.clone(node)));
		}
		const componentsTree = this.getRootElement('tree', Manifest.XMLNS.CM, false);
		const currentTree: Component.ITreeNode[] = [];
		if (!componentsTree) {
			if (!inherited) {
				this.throwUserError('Mandatory tree element not found!', {});
			}
		} else {
			Manifest.generateTreeNodes(
				Array.from(componentsTree.children),
				currentTree,
				this.configurations,
				(this.type === 'manager') ? '' : 'template'
			);
		}
		if (currentTree.length) {
			if (this.treeNodes.length) {
				// Trees need to be merged.
				const slotToNode: Record<string, Component.ITreeNode[]> = {};
				// Map all current tree node to slots.
				for (const node of currentTree) {
					if (!node.slot) {
						node.slot = 'default';
					}
					if (!slotToNode[node.slot]) {
						slotToNode[node.slot] = [];
					}
					const store = slotToNode[node.slot];
					if (!store) {
						// ts hallucination, cannot go here.
						throw new Error;
					}
					store.push(node);
				}
				// Fill slots with nodes.
				STD.object.visit(
					this.treeNodes,
					(container: any, path) => {
						if (
							Number.isNaN(Number.parseInt(path.split('.').slice(-1)[0] || '')) ||
							!container.id
						) {
							// Ignore non node entry.
							return;
						}
						const node: Component.ITreeNode = container;
						if (node.isSlot) {
							if (!node.declaredSlot) {
								this.throwLibraryError('');
							}
							const store = slotToNode[node.declaredSlot];
							if (store) {
								node.childs.push(...store);
								delete slotToNode[node.declaredSlot]; // Delete inserted nodes to see which are not.
							}
						}
					}
				);
				const notInserted = Object.entries(slotToNode);
				if (notInserted.length) {
					this.throwUserError(`Slots not found<${notInserted.map(x => x[0]).join(', ')}>`)
				}
			} else {
				this.treeNodes.push(...currentTree);
			}
		}
	};

	//todo: refactor this method to do only one thing, and there are bugs inside
	private wildcardSelectorToPaths(
		selector: string[],
		allPaths: string[]
	): string[] {
		const imax = selector.length - 1;
		if (
			(imax === 0) ||
			!(<string>selector[0]).endsWith('.') ||
			!selector.slice(1, -1).every(x => x.startsWith('.') && x.endsWith('.')) ||
			(!(<string>selector[imax]).startsWith('.') && (<string>selector[imax] !== ''))
		) {
			throw STD.errors.user('', {
				selector,
				allPaths
			});
		}
		selector = selector.map((x, i) => (
			(i === 0) ? x.slice(0, -1) :
				(i === imax) ? x.slice(1, x.length) :
					x.slice(1, -1)
		));
		const stack: [string[], string[]][] = [[selector, allPaths]];
		const foundPaths: string[] = [];
		while (stack.length !== 0) {
			const [selector, paths] = <Required<typeof stack[0]>>stack.shift();
			const part = <string>selector.shift();
			const n = part.split('.').length;
			const max = selector.length === 0 ? 0 : 1;
			const ckeys = paths.filter(x =>
				x.startsWith(part) &&
				(x.split('.').slice(n).length === max)
			);
			const possiblePaths = paths.filter(x => ckeys.some(y => x.startsWith(y)));
			if ((selector.length === 0) || ((selector.length === 1) && (selector[0] === ''))) {
				foundPaths.push(...ckeys);
			} else {
				const newPart = selector.shift();
				const prefixes = ckeys.map(x => x + '.' + newPart);
				for (const prefix of prefixes) {
					stack.push([
						[prefix, ...selector],
						possiblePaths
					]);
				}
			}
		}
		return (foundPaths);
	};

	private generateConfigurations_selectors(
		configurationsStore: object,
		configurationsKeys: string[]
	) {
		if (!(configurationsStore instanceof Array)) {
			this.throwUserError('The configuration store is not an array!', {
				configurationsStore
			});
		}
		for (const cfg of configurationsStore) {
			const rawSelectors: string[] = cfg.selectors ? cfg.selectors : [cfg.selector];
			const selectors: string[] = [];
			for (const rawSelector of rawSelectors) {
				if (rawSelector.startsWith('.')) {
					selectors.push(...configurationsKeys.filter(x => x.endsWith(rawSelector)));
				} else if (rawSelector.includes('**')) {
					this.dynamicConfigurations[rawSelector] = Object.assign({}, cfg.configuration);
				} else if (rawSelector.includes('*')) {
					selectors.push(...this.wildcardSelectorToPaths(
						rawSelector.split('*'),
						Array.from(configurationsKeys)
					));
				} else {
					selectors.push(rawSelector);
				}
			}
			for (const selector of selectors) {
				if (!this.configurations[selector]) {
					// throw E.user(
					// 	`No component found for path<${ selector }> in selectors!`,
					// 	{
					// 		path           : selector,
					// 		selectors      : cfg,
					// 		configurations : this.configurations
					// 	}
					// );
					this.configurations[selector] = Object.assign({}, cfg.configuration);
				} else {
					// Object.assign(<IComponentConfiguration>this.configurations[ selector ], O.deepMerge.all(
					STD.object.deepMergeAssign(
						<Component.IConfiguration>this.configurations[selector],
						[cfg.configuration]
					);
				}
			}
		}
	};

	private generateConfigurations_dictionnary(
		configurationsStore: object,
		configurationsKeys: string[]
	) {
		void configurationsKeys;
		if (configurationsStore.constructor !== Object) {
			this.throwUserError('The configuration store is not a regular object!', {
				configurationsStore
			});
		}
		const entries = <[string, any][]>Object.entries(configurationsStore);
		//check duplicata
		// Cancelled for now
		// if (rawManifest.data.optimize) {
		// 	const _c : [ string, string[] ][] = [];
		// 	for (let [ path, configuration ] of entries) {
		// 		const cText = JSON.stringify(configuration);
		// 		const duplicata = _c.find(x => x[0] === cText);
		// 		if (duplicata) {
		// 			duplicata[1].push(path);
		// 		} else {
		// 			_c.push([ cText, [ path ] ]);
		// 		}
		// 	}
		// 	const duplicata = _c.map(x => x[1]).filter(x => x.length > 1);
		// 	void duplicata;
		// 	if ((<any>window).serverParams && (<any>window).serverParams.isTestMode) {
		// 		console.log({ duplicata });
		// 	}
		// }
		//assign configuration
		for (const [path, configuration] of entries) {
			if (!this.configurations[path]) {
				if (configuration['@isDeferred']) {
					this.deferedConfigurations[path] = configuration;
				} else {
					this.configurations[path] = Object.assign({}, configuration);
				}
			} else {
				STD.object.deepMergeAssign(<object>this.configurations[path], [<any>configuration]);
			}
		}
	};

	private generateConfigurations() {
		const configurationsKeys = Object.keys(this.configurations);
		const configurationsContainerElement = this.getMandatoryRootElement('configuration', Manifest.XMLNS.CM);
		if (this.inheritedManifest) {
			STD.object.deepMergeAssign(this.configurations, [this.inheritedManifest.configurations]);
			STD.object.deepMergeAssign(this.dynamicConfigurations, [this.inheritedManifest.dynamicConfigurations]);
			STD.object.deepMergeAssign(this.deferedConfigurations, [this.inheritedManifest.deferedConfigurations]);
		}
		for (const scriptJSON of <HTMLScriptElement[]>Array.from(configurationsContainerElement.children)) {
			if (
				(scriptJSON.localName !== 'script') ||
				(scriptJSON.namespaceURI !== Manifest.XMLNS.XHTML)
			) {
				this.throwUserError(
					'configuration element should only contains xhtml:script elements!',
					{scriptJSON}
				);
			}
			const source = scriptJSON.textContent;
			if (!source || source.length === 0) {
				continue;
			}
			try {
				const configurationsStore = JSON.parse(source);
				if (!configurationsStore || (typeof configurationsStore !== 'object')) {
					this.throwUserError('The configuration store is not a valid object!', {
						configurationsStore
					});
				}
				const f = (
					(scriptJSON.dataset.type === 'selectors') ? this.generateConfigurations_selectors :
						this.generateConfigurations_dictionnary
				);
				f.call(this, configurationsStore, configurationsKeys);
			} catch (e: any) {
				// todo : adapt debug infos to use real error position in source code
				throw STD.errors.bind(
					'Cannot parse JSON configuration!',
					{
						manifest: this,
						source,
						scriptJSON
					},
					e
				);
			}
		}
	};

	/* Return the instance but template call already substitued so no need to insert it. */
	MP_template_specialize(
		templateCall: Element,
		template: Element,
		componentId: string
	) {
		const instance = <Element>template.cloneNode(true);
		instance.details = {};
		// if (!instance.details) {
		// 	this.throwLibraryError(`Instance details not initialized!`, {
		// 		templateCall,
		// 		template,
		// 		componentId,
		// 		instance
		// 	});
		// }
		instance.removeAttributeNS(Manifest.XMLNS.MP, 'template-declaration');
		instance.setAttribute('id', componentId);
		templateCall.replaceWith(instance);
		// const cmSlot = instance.querySelector('*|template-slot');
		// if (!cmSlot) {
		// 	this.throwUserError(`No template slot found!`, {
		// 		templateCall,
		// 		template,
		// 		componentId
		// 	});
		// }
		if (!Manifest.isMPData(templateCall, 'template-instance')) { // if is a template not a template-instance <[xx]> ... Yes
			const cmSlot = instance.querySelector('*|template-slot');
			if (!cmSlot || (cmSlot.namespaceURI !== Manifest.XMLNS.MP)) {
				this.throwUserError('No template slot found!', {
					templateCall,
					template,
					componentId,
					errorCode: 'NotFound'
				});
			}
			// templateCall.replaceWith(instance);
			cmSlot.replaceWith(templateCall);
			if (cmSlot.id) {
				templateCall.id = cmSlot.id;
			}
			templateCall.removeAttributeNS(Manifest.XMLNS.MP, 'template');
			const templateDeclaration = templateCall.getAttributeNS(Manifest.XMLNS.MP, 'template-declaration');
			if (templateDeclaration) {
				templateCall.removeAttributeNS(Manifest.XMLNS.MP, 'template-declaration');
				instance.setAttributeNS(Manifest.XMLNS.MP, 'template-declaration', templateDeclaration);
			}
		}
		instance.details.template = template;
		return (instance);
	};

	MP_template_find(templateId: string, forceRefresh = false) {
		let templateNode: Element | undefined;
		if (!forceRefresh && this.templates.length) {
			templateNode = this.templates.find(x => x?.details?.attributesMP.find((y: Manifest.IMPAttribute) => (
				(y.key === 'template-declaration') &&
				(y.value === templateId)
			)));
		} else {
			const templatesNodes = (
				Array.from(this.finalXML.querySelectorAll('templates')).filter(x => x.namespaceURI === Manifest.XMLNS.MP)
			);
			const templateNodes = templatesNodes.map(x => Array.from(x.children)).flat();
			templateNode = templateNodes.find(x =>
				x.getAttributeNS(Manifest.XMLNS.MP, 'template-declaration') === templateId
			);
		}
		if (!templateNode) {
			this.throwUserError(`Template<${templateId}> not found!`, {
				errorCode: 'NotFound'
			});
		}
		return (templateNode);
	};

	private MP_template_findAndSpecialize(
		templateCall: Element,
		templateId: string,
		componentId: string  // id not necessarily stored on component because for template call id is not the id of the component but the slot id.
	) {
		const template = this.MP_template_find(templateId);
		return (this.MP_template_specialize(templateCall, template, componentId));
	};

	private MP_templateInstance_instantiate(element: Element, state: Manifest.IMPState) {
		const _templateId = element.getAttributeNS(Manifest.XMLNS.MP, 'template');
		if (!_templateId) {
			state.incompleteItems.push(element);
			state.unresolvedTemplatesInstances.push(element);
		} else {
			const [templateId, componentId] = (() => {
				if (_templateId.includes(':')) {
					return (_templateId.split(':'));
				} else {
					return ([_templateId, element.id || _templateId]);
				}
			})();
			if (!templateId || !componentId) {
				this.throwUserError('SyntaxError', {});
			}
			const instance = this.MP_template_findAndSpecialize(
				element,
				templateId,
				componentId
			);
			if (!instance.details) {
				this.throwLibraryError('Instance details not initialized!', {
					element,
					templateId,
					componentId,
					instance
				});
			}
			this.MP_annotate(instance);
			if (instance.details.template.isNeedingMultiplePasse) {
				state.incompleteItems.push(instance);
			}
			instance.removeAttributeNS(Manifest.XMLNS.MP, 'template-declaration');
			instance.removeAttributeNS(Manifest.XMLNS.MP, 'template');
			// const templateDeclarationAttributeIdx =;
			// if (templateDeclarationAttributeIdx >= 0) {
			// 	instance.details.attributesMP.splice(templateDeclarationAttributeIdx, 1);
			// }
			const templateAttr = instance.details.attributesMP.find(
				(x: { key: string }) => x.key === 'template'
			);
			const templateAttributeIdx = instance.details.attributesMP.indexOf(
				templateAttr
			);
			if (templateAttributeIdx >= 0) {
				instance.details.attributesMP.splice(templateAttributeIdx, 1);
			}
			state.somethingDone = true;
		}
	};

	private MP_annotate(element: Element) {
		STD.DOM.visitSync(element, el => {
			const details = el.details || (el.details = {});
			if (details.uid === undefined) {
				details.uid = uid++;
			}
			if (details.attributesMP === undefined) {
				details.attributesMP = Manifest.retrieveMPAttributes(el);
			}
		});
	};

	private MP_initialize() {
		// const templates : Element[] = [];
		this.MP_annotate(this.finalXML);
		//templates is all times empty!
		// const accu = {
		// 	isNeedingMultiplePasse : false
		// };
		// console.log({ templates });
		// for (let template of templates) {
		// 	if (!template.details) {
		// 		this.throwLibraryError(RangeError, 'Template details not initialized!', {
		// 			template
		// 		});
		// 	}
		// 	if (
		// 		!template.parentElement ||
		// 		Manifest.isMPData(template.parentElement, 'templates')
		// 	) {
		// 		this.throwUserError(RangeError, 'Template should be a child of templates!', {
		// 			template
		// 		});
		// 	}
		// 	accu.isNeedingMultiplePasse = false;
		// 	visitSync(template, ((a : typeof accu, el : Element) => {
		// 		if (el === template) {
		// 			return;
		// 		}
		// 		if (!el.details) {
		// 			this.throwLibraryError(RangeError, 'Element details not initialized!', {
		// 				element : el,
		// 				template
		// 			});
		// 		}
		// 		if (el.details.attributesMP.length) {
		// 			a.isNeedingMultiplePasse = true;
		// 		}
		// 	}).bind(null, accu));
		// 	template.details.isNeedingMultiplePasse = accu.isNeedingMultiplePasse;
		// }
	};

	private MP_run() {
		const state: Manifest.IMPState = {
			manifest: this.finalXML,
			forceRecursion: false,
			somethingDone: false,
			incompleteItems: [],
			templatesContainers: [],
			unresolvedTemplatesInstances: []
		};
		STD.DOM.visitSync(this.finalXML, (element) => {
			try {
				if (!element.details) {
					this.throwLibraryError('Not initialized element details!', {
						element
					});
				}
				if (element.namespaceURI === Manifest.XMLNS.MP) {
					switch (element.tagName) {
						case 'mp:templates' : {
							state.templatesContainers.push(element);
						}
							break;
						case 'mp:map' : {
							const context = element.getAttribute('context');
							const target = element.getAttribute('target');
							if (!context) {
								this.throwUserError('Cannot use mp:map without context!');
							}
							if (!target) {
								this.throwUserError('Cannot use mp:map without target!');
							}
							if (!element.parentElement) {
								this.throwUserError('SyntaxError: Cannot use mp:map without parent!');
							}
							const dataSourcesGetter = this.dataSourcesGetter[context];
							if (!dataSourcesGetter) {
								this.throwUserError('Cannot use ecommerce mapping if ecommerce not used!');
							}
							const els: Element[] = dataSourcesGetter(target);
							const df = document.createDocumentFragment();
							const parent = element.parentElement;
							for (const c of Array.from(element.children)) { // living iterators, copy it before usage
								df.appendChild(c);
							}
							const insertBefore: Element | null = element.nextElementSibling;
							element.remove();
							if (element.parentElement) {
								this.throwUserError('TS HELPER');
							}
							for (const el of els) {
								const mapped = <DocumentFragment>df.cloneNode(true);
								for (const c of Array.from(mapped.children)) {
									STD.DOM.visitSync(c, x => {
										if (!x.details) {
											this.throwLibraryError('Element.details is not initialized!', {
												element: x
											});
										}
										x.details.mappingContext = el;
									});
									parent.insertBefore(c, insertBefore);
								}
								this.MP_annotate(parent);
							}
							state.somethingDone = true;
							state.forceRecursion = true; // No way to known efficiently if childs need to be worked so do it.
						}
							break;
						case 'mp:call' : {
							if (!element.parentElement) {
								this.throwUserError('SyntaxError: mp:call should have a parent!', {
									element
								});
							}
							if (element.parentElement.children.length !== 1) {
								this.throwUserError('SyntaxError: mp:call should be the only child!', {
									element
								});
							}
							const methodName = element.getAttribute('method');
							if (!methodName) {
								this.throwUserError('SyntaxError: mp:call without method attribute!', {
									element
								});
							}

							if (!state.manifest.details) {
								this.throwUserError('SyntaxError: State manifest without details!', {
									element,
									state
								});
							}
							if (!state.manifest.details.MPContainer) {
								this.throwUserError('SyntaxError: mp:call without metaProgramming container!', {
									element
								});
							}
							const method = state.manifest.details.MPContainer[methodName];
							if (!method) {
								this.throwUserError('SyntaxError: mp:call method not found!', {
									MPContainer: state.manifest.details.MPContainer,
									element,
									methodName
								});
							}
							const r = method(element);
							if (Manifest.isMPData(element.parentElement, 'setAttribute')) {
								element.parentElement.setAttribute('value', r);
							} else {
								element.parentElement.textContent = r;
							}
							element.remove();
							state.somethingDone = true;
						}
							break;
						case 'mp:setAttribute' : {
							const value = element.getAttribute('value');
							if (!value) {
								if (element.children.length !== 1) {
									this.throwUserError('SyntaxError: mp:setAttribute should have a text content or only one child!', {
										element
									});
								}
								if (!Manifest.isMPData(<Element>element.children[0], 'call')) {
									this.throwUserError('SyntaxError: mp:setAttribute children should be mp:call!', {
										element
									});
								}
								state.incompleteItems.push(element);
							} else {
								const key = element.getAttribute('key');
								if (!key) {
									this.throwUserError('mp:setAttribute@key not defined!', {
										element
									});
								}
								const type = element.getAttribute('type');
								let targetDOM: Element | null;
								if (type === 'previous') {
									targetDOM = element.previousElementSibling || null;
								} else if (type === 'next') {
									targetDOM = element.nextElementSibling || null;
								} else {
									this.throwUserError('mp:setAttribute@type should be previous or next!', {
										element,
										type
									});
								}
								if (!targetDOM) {
									if (type === 'previous') {
										this.throwUserError('mp:setAttribute: Previous element targeted but there are no such element!', {
											element
										});
									} else {
										this.throwUserError('mp:setAttribute: Next element targeted but there are no such element!', {
											element
										});
									}
								}
								targetDOM.setAttribute(key, value);
								element.remove();
								state.somethingDone = true;
							}
						}
							break;
						case 'mp:template-instance' : {
							this.MP_templateInstance_instantiate(element, state);
						}
							break;
					}
				} else if (element.details.length !== 0) {
					for (const attr of element.details.attributesMP) {
						switch (attr.key) {
							case 'template-declaration' : {
								this.templates.push(element);
							}
								break;
							case 'template' : {
								this.MP_templateInstance_instantiate(element, state);
							}
								break;
							case 'remap' : {
								if (!element.details.mappingContext) {
									this.throwUserError('SyntaxError: Remap attribute should have a mp:map as their ancestor!', {
										element
									});
								}
								const [LHS, RHS] = (
									attr.value.includes('=') ? attr.value.split('=') :
										[attr.value, attr.value]
								);
								if (!LHS || !RHS) {
									this.throwUserError('SyntaxError: Invalid remap attribute value!', {
										LHS, RHS
									});
								}
								if (!LHS.startsWith('#') && !RHS.startsWith('#')) {
									const pathLHS = LHS.split('.');
									const [hostPathLHS, keyLHS] = [pathLHS.slice(0, -1), <string>pathLHS.slice(-1)[0]];
									const hostLHS = STD.object.get(element, hostPathLHS);
									if (!hostLHS || (typeof hostLHS !== 'object')) {
										this.throwUserError('Remap attribute LHS path is invalid!', {
											element,
											LHS,
											hostLHS
										});
									}
									(<Record<string, any>>hostLHS)[keyLHS] = STD.object.get(element.details.mappingContext, RHS.split('.'));
									element.removeAttribute(attr.realKey);
									element.details.attributesMP.splice(
										element.details.attributesMP.indexOf(attr),
										1
									);
									state.somethingDone = true;
								}
							}
								break;
						}
					}
				}
			} catch (e) {
				throw STD.errors.bindFromUnknown('Cannot handle an element!', {
					element,
					manifest: this
				}, e);
			}
		});
		if (!state.unresolvedTemplatesInstances.length) {
			for (const template of this.templates) {
				template.remove();
			}
			for (const container of state.templatesContainers) {
				container.remove();
			}
		}
		if (state.incompleteItems.length || state.forceRecursion) {
			if (!state.somethingDone) {
				this.throwUserError('MetaProgramming not finished but nothing to do to unlock the situation!', {
					incompleteItems: state.incompleteItems
				});
			}
			return true;
		}
		return false;
	};

	private initialize() {
		this.stage = 'initialization';
		this.initializeManifestDetails(this.finalXML);
		this.stage = 'metaprogramming';
		this.MP_initialize();
		while (this.MP_run()) {
			;
			;
		}
		this.stage = 'treeNodes';
		this.generateTreeNodes();
		this.stage = 'configuration';
		this.generateConfigurations();
		this.stage = 'ready';
	};

	protected constructor(
		source: string,
		initialXML: Element,
		type: string,
		inheritedManifest: Manifest | null
	) {
		this.source = source;
		this.initialXML = initialXML;
		this.finalXML = <Element>initialXML.cloneNode(true);
		this.inheritedManifest = inheritedManifest;
		// Handle XHTML attributes
		// Cancelled for now(Heavy work & conception needed + Not blocking)
		// for (let attribute of Array.from(initialXML.attributes)) {
		// 	if (attribute.namespaceURI === )
		// }
		if (!this.type) {
			this.type = this.constructor.prototype.type || 'unknown';
		}
		if (this.type !== type) {
			this.throwUserError(`Type<${type}> is not valid!`, {
				error: this.initialXML
			});
		}
		if (this.initialXML.tagName.toLowerCase() === 'parsererror') {
			this.throwUserError('SyntaxError: ' + (this.initialXML.textContent || 'DOMParser'), {
				error: this.initialXML
			});
		}
	};
};
namespace Manifest {

	export interface IMPState {
		manifest: Element;
		forceRecursion: boolean;
		somethingDone: boolean;
		incompleteItems: Element[];
		templatesContainers: Element[];
		unresolvedTemplatesInstances: Element[];
	};

	export interface IMPAttribute {
		key: string;
		realKey: string;
		value: string;
		attributeNode: Attr;
	};

	export type TManifest = ManagerManifest | ComponentManifest;

	// For ManagerManifest and ComponentManifest, do not use import export = because this cause unresolvable cyclic dependencies.
	export let ManagerManifest: typeof _ManagerManifest;
	export type ManagerManifest = _ManagerManifest;
	export let ComponentManifest: typeof _ComponentManifest;
	export type ComponentManifest = _ComponentManifest;
}
;
export default Manifest;
import Component from './Component';
import DOMComponent from './DOMComponent';
import _ManagerManifest from './manifests/ManagerManifest';
import _ComponentManifest from './manifests/ComponentManifest';
